import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirm } from '@/modules/common/components/useConfirm';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';

import { useIsVehicleBeingUsedCheck } from './useIsVehicleBeingUsedCheck';

type ConfirmVehicleRemoval = ReturnType<typeof useConfirm>['confirm'];

export const useVehicleRemovalGuard = (confirm: ConfirmVehicleRemoval) => {
  const { t } = useTranslation(['interface']);
  const { isVehicleBeingUsed, isVehicleBeingUsedInShuttleRack } = useIsVehicleBeingUsedCheck();
  const { showSnackbar } = useSnackbarStore();

  const showVehicleUsedInShuttleRackWarning = useCallback(
    () =>
      showSnackbar(
        t(
          'interface:setup.floorPlan.vehicle_types_select_dialog.warnings.vehicle_used_in_shuttle_rack',
          'Cannot deselect this vehicle while it is used by a shuttle rack. Delete the shuttle rack first.',
        ),
      ),
    [showSnackbar, t],
  );

  const confirmVehicleRemoval = useCallback(
    (id: string) => {
      const vehicle = useVehicleStore
        .getState()
        .allVehiclesState.find(({ id: vehicleId }) => vehicleId === id);

      return confirm({
        title: t('setup.floorPlan.vehicle_types.delete_button.title', 'Delete vehicle'),
        message: t(
          'setup.floorPlan.vehicle_types.delete_confirm_message',
          'This will remove "{{name}}" from this floorplan and all related usage. Continue?',
          { name: vehicle?.name ?? id },
        ),
      });
    },
    [confirm, t],
  );

  const canRemoveVehicle = useCallback(
    async (id: string): Promise<boolean> => {
      if (isVehicleBeingUsedInShuttleRack(id)) {
        showVehicleUsedInShuttleRackWarning();
        return false;
      }

      if (!isVehicleBeingUsed(id)) {
        return true;
      }

      return confirmVehicleRemoval(id);
    },
    [
      confirmVehicleRemoval,
      isVehicleBeingUsed,
      isVehicleBeingUsedInShuttleRack,
      showVehicleUsedInShuttleRackWarning,
    ],
  );

  return { canRemoveVehicle };
};
